import { Badge } from '@artic/ui/badge';

import type { ImportValidationCounts, PreviewCell, PreviewRow } from './import-validation';

/** A row is actionable when there is something to do: create, or an update with changes. */
export function isActionable(row: PreviewRow): boolean {
  return row.status === 'new' || (row.status === 'update' && row.hasChanges);
}

interface PreviewStepProps {
  previews: PreviewRow[];
  counts: ImportValidationCounts;
  /** sourceIndexes the user has unticked (won't be applied). */
  deselected: ReadonlySet<number>;
  onToggle: (sourceIndex: number) => void;
}

export function PreviewStep({ previews, counts, deselected, onToggle }: PreviewStepProps) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant="secondary">{counts.update} to update</Badge>
        <Badge variant="default">{counts.create} to create</Badge>
        {counts.unchanged > 0 && <Badge variant="outline">{counts.unchanged} unchanged</Badge>}
        {counts.invalid > 0 && (
          <Badge variant="destructive">{counts.invalid} skipped (invalid)</Badge>
        )}
      </div>

      <div className="max-h-[50vh] overflow-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground bg-background sticky top-0 text-left">
            <tr className="border-b">
              <th className="w-10 px-2 py-1.5" />
              <th className="px-2 py-1.5 font-medium">Row</th>
              <th className="px-2 py-1.5 font-medium">Work Order</th>
              <th className="px-2 py-1.5 font-medium">Status</th>
              <th className="px-2 py-1.5 font-medium">Changes</th>
            </tr>
          </thead>
          <tbody>
            {previews.map((row) => {
              const actionable = isActionable(row);
              const selected = actionable && !deselected.has(row.sourceIndex);
              return (
                <tr key={row.sourceIndex} className="border-b align-top last:border-0">
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={!actionable}
                      aria-label={`Apply row ${row.sourceIndex + 1}`}
                      onChange={() => {
                        onToggle(row.sourceIndex);
                      }}
                    />
                  </td>
                  <td className="text-muted-foreground px-2 py-1.5">{row.sourceIndex + 1}</td>
                  <td className="px-2 py-1.5 font-medium">{row.identity || '—'}</td>
                  <td className="px-2 py-1.5">
                    <StatusBadge row={row} />
                  </td>
                  <td className="px-2 py-1.5">
                    <RowDetails row={row} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ row }: { row: PreviewRow }) {
  if (row.status === 'invalid') return <Badge variant="destructive">Invalid</Badge>;
  if (row.status === 'new') return <Badge variant="default">New</Badge>;
  if (!row.hasChanges) return <Badge variant="outline">No change</Badge>;
  return <Badge variant="secondary">Update</Badge>;
}

function RowDetails({ row }: { row: PreviewRow }) {
  if (row.status === 'invalid') {
    return <span className="text-destructive text-xs">{row.errors.join(' ')}</span>;
  }

  const shown = row.cells.filter((c) => cellIsInteresting(c, row.status));
  if (shown.length === 0) {
    return <span className="text-muted-foreground text-xs">No mapped changes</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      {shown.map((cell) => (
        <CellChange key={cell.field} cell={cell} isNew={row.status === 'new'} />
      ))}
    </div>
  );
}

/** Which cells to surface: new rows show set values; updates show changed or locked cells. */
function cellIsInteresting(cell: PreviewCell, status: PreviewRow['status']): boolean {
  if (cell.field === 'work_order_name') return false; // identity shown in its own column
  if (status === 'new') return cell.newDisplay !== '';
  return cell.changed || cell.locked;
}

function CellChange({ cell, isNew }: { cell: PreviewCell; isNew: boolean }) {
  if (cell.locked) {
    return (
      <span className="text-muted-foreground">
        <span className="font-medium">{cell.label}</span>: locked by status — skipped
      </span>
    );
  }
  if (isNew) {
    return (
      <span>
        <span className="font-medium">{cell.label}</span>:{' '}
        <span className="text-green-700 dark:text-green-400">{cell.newDisplay || '(blank)'}</span>
      </span>
    );
  }
  return (
    <span>
      <span className="font-medium">{cell.label}</span>:{' '}
      <span className="text-muted-foreground line-through">{cell.oldDisplay || '(blank)'}</span>{' '}
      <span aria-hidden>→</span>{' '}
      <span className="text-green-700 dark:text-green-400">{cell.newDisplay || '(blank)'}</span>
    </span>
  );
}
